/**
 * VersionComparison Component
 *
 * Displays a comparison view between two extraction versions,
 * highlighting changes in extracted values between versions.
 *
 * Requirements: 9.5, 9.6
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  ArrowRight24Regular,
  Dismiss24Regular,
  DocumentMultiple24Regular,
  ChevronRight20Regular,
  Add16Regular,
  Subtract16Regular,
  Edit16Regular,
  Checkmark16Regular,
  ArrowUp16Regular,
  ArrowDown16Regular,
  LineHorizontal116Regular,
} from '@fluentui/react-icons';
import type { ExtractionSummary } from '../../types/extraction';
import type {
  ExtractionResults,
  GroundRiskAnswer,
  GroundRiskMitigation,
  AirRiskAnswer,
  AirRiskMitigation,
  ConfidenceLevel,
} from '../../types/extraction';
import styles from './VersionComparison.module.css';

// ============================================================================
// Types
// ============================================================================

/** Change type for diff highlighting */
export type ChangeType = 'added' | 'removed' | 'changed' | 'unchanged';

/** Diff item representing a single field comparison */
export interface DiffItem {
  id: string;
  field: string;
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
  oldConfidence?: ConfidenceLevel;
  newConfidence?: ConfidenceLevel;
  changeType: ChangeType;
}

/** Section containing diff items */
export interface DiffSection {
  id: string;
  title: string;
  items: DiffItem[];
  changedCount: number;
}

/** Props for the VersionComparison component */
export interface VersionComparisonProps {
  /** List of available extraction summaries to compare */
  extractions: ExtractionSummary[];
  /** Function to load full extraction results by ID */
  loadExtraction: (extractionId: string) => Promise<ExtractionResults | null>;
  /** Callback when comparison view is closed */
  onClose?: () => void;
  /** Initial left (older) version ID */
  initialLeftVersionId?: string;
  /** Initial right (newer) version ID */
  initialRightVersionId?: string;
}


// ============================================================================
// Constants
// ============================================================================

/** Ground risk field labels */
const GROUND_RISK_FIELD_LABELS: Record<string, string> = {
  max_dimensions: 'Maximum Dimensions',
  max_speed_mps: 'Maximum Speed (m/s)',
  weight_over_250g: 'Weight Over 250g',
  controlled_ground_area_only: 'Controlled Ground Area Only',
  max_population_density: 'Maximum Population Density',
};

/** Ground risk mitigation labels */
const GROUND_MITIGATION_LABELS: Record<string, string> = {
  M1A: 'M1A - Sheltering',
  M1B: 'M1B - Operational Restrictions',
  M1C: 'M1C - Ground Observations',
  M2: 'M2 - Reducing Impact Dynamics',
};

/** Air risk field labels */
const AIR_RISK_FIELD_LABELS: Record<string, string> = {
  aae_only: 'Operating in AAE Only',
  known_ifp_area: 'Known IFP Area',
  vfr_corridor_area: 'VFR Corridor Area',
  above_500ft_agl: 'Above 500ft AGL',
  operation_type: 'Operation Type',
};

/** Air risk mitigation labels */
const AIR_MITIGATION_LABELS: Record<string, string> = {
  SM1: 'SM1 - Operational Restriction by Boundary',
  SM2: 'SM2 - Operational Restriction by Chronology',
  SM3: 'SM3 - Operational Restriction by Time of Exposure',
  SM4: 'SM4 - Special Use Airspace (SUA)',
  SM5: 'SM5 - Other Airspace Requirements',
  SM6: 'SM6 - Pre-agreement of ANSP Services',
  SM7: 'SM7 - Pre-agreement of UTM Services',
  SM8: 'SM8 - NOTAM of Intended Operation',
  SM9: 'SM9 - Military Low Flying Notification',
  SM10: 'SM10 - Outreach to Local Flying Clubs',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Truncates a string to a max length, adding ellipsis if needed
 */
function truncateName(name: string, maxLength = 30): string {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength - 1) + '…';
}

/**
 * Formats a date string for display
 */
function formatDate(dateString: string): string {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Converts a value to a display string
 */
function valueToString(value: string | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Determines the change type between two values
 */
function getChangeType(oldValue: string | null, newValue: string | null): ChangeType {
  if (oldValue === null && newValue !== null) return 'added';
  if (oldValue !== null && newValue === null) return 'removed';
  if (oldValue !== newValue) return 'changed';
  return 'unchanged';
}

/**
 * Compares ground risk answers between two extractions
 */
function compareGroundRiskAnswers(
  oldAnswers: GroundRiskAnswer[] | undefined,
  newAnswers: GroundRiskAnswer[] | undefined
): DiffItem[] {
  const items: DiffItem[] = [];
  const allFields = new Set([
    ...(oldAnswers?.map((a) => a.field) || []),
    ...(newAnswers?.map((a) => a.field) || []),
  ]);

  allFields.forEach((field) => {
    const oldAnswer = oldAnswers?.find((a) => a.field === field);
    const newAnswer = newAnswers?.find((a) => a.field === field);
    const oldValue = oldAnswer ? valueToString(oldAnswer.extractedValue) : null;
    const newValue = newAnswer ? valueToString(newAnswer.extractedValue) : null;

    items.push({
      id: `gra-${field}`,
      field,
      fieldLabel: GROUND_RISK_FIELD_LABELS[field] || field,
      oldValue,
      newValue,
      oldConfidence: oldAnswer?.confidence,
      newConfidence: newAnswer?.confidence,
      changeType: getChangeType(oldValue, newValue),
    });
  });

  return items;
}


/**
 * Compares ground risk mitigations between two extractions
 */
function compareGroundRiskMitigations(
  oldMitigations: GroundRiskMitigation[] | undefined,
  newMitigations: GroundRiskMitigation[] | undefined
): DiffItem[] {
  const items: DiffItem[] = [];
  const allCodes = new Set([
    ...(oldMitigations?.map((m) => m.code) || []),
    ...(newMitigations?.map((m) => m.code) || []),
  ]);

  allCodes.forEach((code) => {
    const oldMit = oldMitigations?.find((m) => m.code === code);
    const newMit = newMitigations?.find((m) => m.code === code);
    const oldValue = oldMit ? oldMit.selectedLevel : null;
    const newValue = newMit ? newMit.selectedLevel : null;

    items.push({
      id: `grm-${code}`,
      field: code,
      fieldLabel: GROUND_MITIGATION_LABELS[code] || code,
      oldValue,
      newValue,
      changeType: getChangeType(oldValue, newValue),
    });
  });

  return items;
}

/**
 * Compares air risk answers between two extractions
 */
function compareAirRiskAnswers(
  oldAnswers: AirRiskAnswer[] | undefined,
  newAnswers: AirRiskAnswer[] | undefined
): DiffItem[] {
  const items: DiffItem[] = [];
  const allFields = new Set([
    ...(oldAnswers?.map((a) => a.field) || []),
    ...(newAnswers?.map((a) => a.field) || []),
  ]);

  allFields.forEach((field) => {
    const oldAnswer = oldAnswers?.find((a) => a.field === field);
    const newAnswer = newAnswers?.find((a) => a.field === field);
    const oldValue = oldAnswer ? valueToString(oldAnswer.extractedValue) : null;
    const newValue = newAnswer ? valueToString(newAnswer.extractedValue) : null;

    items.push({
      id: `ara-${field}`,
      field,
      fieldLabel: AIR_RISK_FIELD_LABELS[field] || field,
      oldValue,
      newValue,
      oldConfidence: oldAnswer?.confidence,
      newConfidence: newAnswer?.confidence,
      changeType: getChangeType(oldValue, newValue),
    });
  });

  return items;
}

/**
 * Compares air risk mitigations between two extractions
 */
function compareAirRiskMitigations(
  oldMitigations: AirRiskMitigation[] | undefined,
  newMitigations: AirRiskMitigation[] | undefined
): DiffItem[] {
  const items: DiffItem[] = [];
  const allCodes = new Set([
    ...(oldMitigations?.map((m) => m.code) || []),
    ...(newMitigations?.map((m) => m.code) || []),
  ]);

  allCodes.forEach((code) => {
    const oldMit = oldMitigations?.find((m) => m.code === code);
    const newMit = newMitigations?.find((m) => m.code === code);
    const oldValue = oldMit ? valueToString(oldMit.selectedValue) : null;
    const newValue = newMit ? valueToString(newMit.selectedValue) : null;

    items.push({
      id: `arm-${code}`,
      field: code,
      fieldLabel: AIR_MITIGATION_LABELS[code] || code,
      oldValue,
      newValue,
      changeType: getChangeType(oldValue, newValue),
    });
  });

  return items;
}

/**
 * Generates all diff sections from two extraction results
 */
function generateDiffSections(
  oldExtraction: ExtractionResults | null,
  newExtraction: ExtractionResults | null
): DiffSection[] {
  const sections: DiffSection[] = [];

  // Ground Risk Answers
  const groundRiskAnswers = compareGroundRiskAnswers(
    oldExtraction?.groundRiskAnswers,
    newExtraction?.groundRiskAnswers
  );
  sections.push({
    id: 'ground-risk-answers',
    title: 'Ground Risk Answers',
    items: groundRiskAnswers,
    changedCount: groundRiskAnswers.filter((i) => i.changeType !== 'unchanged').length,
  });

  // Ground Risk Mitigations
  const groundRiskMitigations = compareGroundRiskMitigations(
    oldExtraction?.groundRiskMitigations,
    newExtraction?.groundRiskMitigations
  );
  sections.push({
    id: 'ground-risk-mitigations',
    title: 'Ground Risk Mitigations',
    items: groundRiskMitigations,
    changedCount: groundRiskMitigations.filter((i) => i.changeType !== 'unchanged').length,
  });

  // Air Risk Answers
  const airRiskAnswers = compareAirRiskAnswers(
    oldExtraction?.airRiskAnswers,
    newExtraction?.airRiskAnswers
  );
  sections.push({
    id: 'air-risk-answers',
    title: 'Air Risk Answers',
    items: airRiskAnswers,
    changedCount: airRiskAnswers.filter((i) => i.changeType !== 'unchanged').length,
  });

  // Air Risk Mitigations
  const airRiskMitigations = compareAirRiskMitigations(
    oldExtraction?.airRiskMitigations,
    newExtraction?.airRiskMitigations
  );
  sections.push({
    id: 'air-risk-mitigations',
    title: 'Air Risk Mitigations',
    items: airRiskMitigations,
    changedCount: airRiskMitigations.filter((i) => i.changeType !== 'unchanged').length,
  });

  return sections;
}


// ============================================================================
// Sub-Components
// ============================================================================

interface StatusBadgeProps {
  changeType: ChangeType;
}

/**
 * Displays a badge indicating the type of change
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ changeType }) => {
  const config = {
    added: { label: 'Added', icon: Add16Regular, className: styles.statusAdded },
    removed: { label: 'Removed', icon: Subtract16Regular, className: styles.statusRemoved },
    changed: { label: 'Changed', icon: Edit16Regular, className: styles.statusChanged },
    unchanged: { label: 'Unchanged', icon: Checkmark16Regular, className: styles.statusUnchanged },
  };

  const { label, icon: Icon, className } = config[changeType];

  return (
    <span className={`${styles.statusBadge} ${className}`}>
      <Icon />
      {label}
    </span>
  );
};

interface ConfidenceChangeProps {
  oldConfidence?: ConfidenceLevel;
  newConfidence?: ConfidenceLevel;
}

/**
 * Displays confidence level change indicator
 */
const ConfidenceChange: React.FC<ConfidenceChangeProps> = ({ oldConfidence, newConfidence }) => {
  if (!oldConfidence && !newConfidence) return null;

  const levels = { low: 1, medium: 2, high: 3 };
  const oldLevel = oldConfidence ? levels[oldConfidence] : 0;
  const newLevel = newConfidence ? levels[newConfidence] : 0;

  let Icon = LineHorizontal116Regular;
  let className = styles.confidenceSame;

  if (newLevel > oldLevel) {
    Icon = ArrowUp16Regular;
    className = styles.confidenceUp;
  } else if (newLevel < oldLevel) {
    Icon = ArrowDown16Regular;
    className = styles.confidenceDown;
  }

  return (
    <span className={styles.confidenceChange}>
      <Icon className={`${styles.confidenceArrow} ${className}`} />
      <span>{newConfidence || oldConfidence}</span>
    </span>
  );
};

interface DiffRowProps {
  item: DiffItem;
}

/**
 * Renders a single row in the comparison table
 */
const DiffRow: React.FC<DiffRowProps> = ({ item }) => {
  const rowClass = {
    added: styles.rowAdded,
    removed: styles.rowRemoved,
    changed: styles.rowChanged,
    unchanged: '',
  }[item.changeType];

  const getValueClass = (type: 'old' | 'new') => {
    if (item.changeType === 'unchanged') return '';
    if (type === 'old' && item.changeType === 'removed') return styles.highlightRemoved;
    if (type === 'new' && item.changeType === 'added') return styles.highlightAdded;
    if (item.changeType === 'changed') return styles.highlightChanged;
    return '';
  };

  return (
    <tr className={`${styles.diffRow} ${rowClass}`}>
      <td className={styles.fieldColumn}>
        <span className={styles.fieldName}>{item.fieldLabel}</span>
      </td>
      <td className={styles.valueColumn}>
        <span className={`${styles.valueCell} ${styles.valueOld}`}>
          {item.oldValue ? (
            <span className={`${styles.valueHighlight} ${getValueClass('old')}`}>
              {item.oldValue}
            </span>
          ) : (
            <span className={styles.emptyValue}>—</span>
          )}
        </span>
      </td>
      <td className={styles.valueColumn}>
        <span className={`${styles.valueCell} ${styles.valueNew}`}>
          {item.newValue ? (
            <span className={`${styles.valueHighlight} ${getValueClass('new')}`}>
              {item.newValue}
            </span>
          ) : (
            <span className={styles.emptyValue}>—</span>
          )}
        </span>
      </td>
      <td className={styles.statusColumn}>
        <StatusBadge changeType={item.changeType} />
      </td>
    </tr>
  );
};

interface ComparisonSectionProps {
  section: DiffSection;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Renders a collapsible section of the comparison
 */
const ComparisonSection: React.FC<ComparisonSectionProps> = ({
  section,
  isExpanded,
  onToggle,
}) => {
  return (
    <div className={styles.section}>
      <div
        className={styles.sectionHeader}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-controls={`section-content-${section.id}`}
      >
        <ChevronRight20Regular
          className={`${styles.sectionIcon} ${isExpanded ? styles.sectionIconExpanded : ''}`}
        />
        <h3 className={styles.sectionTitle}>{section.title}</h3>
        <span
          className={`${styles.sectionBadge} ${
            section.changedCount > 0 ? styles.badgeChanged : styles.badgeUnchanged
          }`}
        >
          {section.changedCount > 0 ? `${section.changedCount} changed` : 'No changes'}
        </span>
      </div>
      {isExpanded && (
        <div className={styles.sectionContent} id={`section-content-${section.id}`}>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th className={styles.fieldColumn}>Field</th>
                <th className={styles.valueColumn}>Document A</th>
                <th className={styles.valueColumn}>Document B</th>
                <th className={styles.statusColumn}>Status</th>
              </tr>
            </thead>
            <tbody>
              {section.items.map((item) => (
                <DiffRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Main Component
// ============================================================================

/**
 * VersionComparison displays a side-by-side comparison of two extraction versions.
 *
 * Features:
 * - Version selector dropdowns to choose which versions to compare
 * - Summary statistics showing counts of added, removed, changed, and unchanged items
 * - Collapsible sections for each category (Ground Risk Answers, Mitigations, etc.)
 * - Color-coded diff highlighting for changes
 * - Status badges indicating the type of change for each field
 *
 * Requirements:
 * - 9.5: Users can compare extraction results between versions
 * - 9.6: Differences between versions are highlighted
 *
 * @example
 * <VersionComparison
 *   extractions={application.extractions}
 *   loadExtraction={loadExtractionById}
 *   onClose={() => setShowComparison(false)}
 * />
 */
export const VersionComparison: React.FC<VersionComparisonProps> = ({
  extractions,
  loadExtraction,
  onClose,
  initialLeftVersionId,
  initialRightVersionId,
}) => {
  // Sort extractions by version (oldest first for left, newest for right)
  const sortedExtractions = useMemo(
    () => [...extractions].sort((a, b) => a.version - b.version),
    [extractions]
  );

  // State for selected versions
  const [leftVersionId, setLeftVersionId] = useState<string>(
    initialLeftVersionId || (sortedExtractions.length > 1 ? sortedExtractions[0].id : '')
  );
  const [rightVersionId, setRightVersionId] = useState<string>(
    initialRightVersionId ||
      (sortedExtractions.length > 0 ? sortedExtractions[sortedExtractions.length - 1].id : '')
  );

  // State for loaded extraction data
  const [leftExtraction, setLeftExtraction] = useState<ExtractionResults | null>(null);
  const [rightExtraction, setRightExtraction] = useState<ExtractionResults | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['ground-risk-answers', 'ground-risk-mitigations', 'air-risk-answers', 'air-risk-mitigations'])
  );

  // Load extractions when versions change
  const loadExtractions = useCallback(async () => {
    if (!leftVersionId || !rightVersionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [left, right] = await Promise.all([
        loadExtraction(leftVersionId),
        loadExtraction(rightVersionId),
      ]);

      setLeftExtraction(left);
      setRightExtraction(right);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load extractions');
    } finally {
      setIsLoading(false);
    }
  }, [leftVersionId, rightVersionId, loadExtraction]);

  // Load extractions on mount and when versions change
  React.useEffect(() => {
    loadExtractions();
  }, [loadExtractions]);

  // Generate diff sections
  const diffSections = useMemo(
    () => generateDiffSections(leftExtraction, rightExtraction),
    [leftExtraction, rightExtraction]
  );

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const allItems = diffSections.flatMap((s) => s.items);
    return {
      added: allItems.filter((i) => i.changeType === 'added').length,
      removed: allItems.filter((i) => i.changeType === 'removed').length,
      changed: allItems.filter((i) => i.changeType === 'changed').length,
      unchanged: allItems.filter((i) => i.changeType === 'unchanged').length,
    };
  }, [diffSections]);

  // Toggle section expansion
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Get extraction summary by ID
  const getExtractionSummary = useCallback(
    (id: string) => extractions.find((e) => e.id === id),
    [extractions]
  );

  // Handle version selection
  const handleLeftVersionChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setLeftVersionId(e.target.value);
    },
    []
  );

  const handleRightVersionChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setRightVersionId(e.target.value);
    },
    []
  );

  // Render empty state if not enough versions
  if (extractions.length < 2) {
    return (
      <div className={styles.versionComparison}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <div className={styles.headerIcon}>
              <DocumentMultiple24Regular />
            </div>
            <h2 className={styles.title}>Version Comparison</h2>
          </div>
          {onClose && (
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close comparison"
            >
              <Dismiss24Regular />
            </button>
          )}
        </div>
        <div className={styles.emptyState}>
          <DocumentMultiple24Regular className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>Not Enough Versions</h3>
          <p className={styles.emptyText}>
            At least two extraction versions are required to compare.
            Process documents again to create a new version.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.versionComparison}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <div className={styles.headerIcon}>
            <DocumentMultiple24Regular />
          </div>
          <h2 className={styles.title}>Version Comparison</h2>
        </div>
        {onClose && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close comparison"
          >
            <Dismiss24Regular />
          </button>
        )}
      </div>

      {/* Version Selectors */}
      <div className={styles.versionSelectors}>
        <div className={styles.selectorGroup}>
          <label className={styles.selectorLabel} htmlFor="left-version">
            Document A
          </label>
          <select
            id="left-version"
            className={styles.selector}
            value={leftVersionId}
            onChange={handleLeftVersionChange}
          >
            {sortedExtractions.map((ext) => (
              <option key={ext.id} value={ext.id}>
                {ext.displayName ? truncateName(ext.displayName) : `Document ${ext.version}`} — {formatDate(ext.processedAt)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.selectorArrow}>
          <ArrowRight24Regular />
        </div>

        <div className={styles.selectorGroup}>
          <label className={styles.selectorLabel} htmlFor="right-version">
            Document B
          </label>
          <select
            id="right-version"
            className={styles.selector}
            value={rightVersionId}
            onChange={handleRightVersionChange}
          >
            {sortedExtractions.map((ext) => (
              <option key={ext.id} value={ext.id}>
                {ext.displayName ? truncateName(ext.displayName) : `Document ${ext.version}`} — {formatDate(ext.processedAt)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span className={styles.loadingText}>Loading extraction data...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>Error Loading Data</h3>
          <p className={styles.emptyText}>{error}</p>
        </div>
      )}

      {/* Comparison Content */}
      {!isLoading && !error && leftExtraction && rightExtraction && (
        <>
          {/* Summary Statistics */}
          <div className={styles.summaryStats}>
            <div className={styles.statItem}>
              <Add16Regular className={`${styles.statIcon} ${styles.statAdded}`} />
              <span className={styles.statLabel}>Added:</span>
              <span className={`${styles.statCount} ${styles.statAdded}`}>
                {summaryStats.added}
              </span>
            </div>
            <div className={styles.statItem}>
              <Subtract16Regular className={`${styles.statIcon} ${styles.statRemoved}`} />
              <span className={styles.statLabel}>Removed:</span>
              <span className={`${styles.statCount} ${styles.statRemoved}`}>
                {summaryStats.removed}
              </span>
            </div>
            <div className={styles.statItem}>
              <Edit16Regular className={`${styles.statIcon} ${styles.statChanged}`} />
              <span className={styles.statLabel}>Changed:</span>
              <span className={`${styles.statCount} ${styles.statChanged}`}>
                {summaryStats.changed}
              </span>
            </div>
            <div className={styles.statItem}>
              <Checkmark16Regular className={`${styles.statIcon} ${styles.statUnchanged}`} />
              <span className={styles.statLabel}>Unchanged:</span>
              <span className={`${styles.statCount} ${styles.statUnchanged}`}>
                {summaryStats.unchanged}
              </span>
            </div>
          </div>

          {/* Comparison Sections */}
          <div className={styles.comparisonContent}>
            {diffSections.map((section) => (
              <ComparisonSection
                key={section.id}
                section={section}
                isExpanded={expandedSections.has(section.id)}
                onToggle={() => toggleSection(section.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VersionComparison;
